import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { ticketContractQuery, type TicketContractRow } from '../api/easeedesk'
import { Spinner } from '../ui/Spinner'
import './TicketContract.css'

/**
 * Fila "Contract" - `easeedesk/tickets/contracts/index.php`.
 *
 * Intrebarea filei e una singura: TICHETUL ASTA E ACOPERIT DE UN CONTRACT? Se
 * arata contractele clientului care il acopera, si in fiecare toate serviciile,
 * cu semn pe cele care se potrivesc pe tipul tichetului.
 *
 * Raspunsul se citeste de sus: cand nu exista niciun contract care sa acopere
 * tichetul, asta e chiar vestea, si se spune - in portal ramanea un panou gol,
 * care semana cu unul care nu s-a incarcat.
 */

export function TicketContract({ id }: { id: number }) {
  const { data, error } = useQuery(ticketContractQuery(id))
  const [open, setOpen] = useState(0)

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {error.message}
      </div>
    )
  }
  if (!data) return <Spinner />

  if (data.contracts.length === 0) {
    return (
      <div className="alert alert-warning" role="alert">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> No contract of this customer covers this kind
        of ticket.
      </div>
    )
  }

  /* Primul contract deschis: in portal toate porneau inchise, si pe un client
     cu un singur contract trebuia apasat ca sa se vada singurul raspuns. */
  const shown = data.contracts[Math.min(open, data.contracts.length - 1)]

  return (
    <div className="panel-group ct-wrap">
      {data.contracts.map((contract, i) => (
        <div className="panel panel-default" key={contract.id}>
          <div className="panel-heading">
            <p className="panel-title color-light-grey ct-title" onClick={() => setOpen(i)}>
              <i className={`fa fa-chevron-circle-${shown.id === contract.id ? 'down' : 'right'} color-easedeesk`}></i>{' '}
              Contract number: {contract.number}
              {contract.type !== '' && <span className="ct-type"> ({contract.type})</span>}
              <i className="fas fa-file-alt color-easedeesk pull-right"></i>
            </p>
          </div>

          {shown.id === contract.id && <Body contract={contract} domains={data.domains} modules={data.inactiveModules} />}
        </div>
      ))}
    </div>
  )
}

function Body({
  contract,
  domains,
  modules,
}: {
  contract: TicketContractRow
  domains: string[]
  modules: { name: string; icon: string }[]
}) {
  const [showModules, setShowModules] = useState(false)

  return (
    <div className="panel-body">
      <table className="table table-hover table-striped table-bordered">
        <thead>
          <tr>
            <th>Service Name</th>
            <th>Service Category</th>
            <th>Coverage</th>
            <th>Intervention SLA</th>
            <th>Device Number</th>
            <th>Has Services</th>
          </tr>
        </thead>
        <tbody>
          {contract.services.map((service) => (
            <tr key={service.id}>
              <td title={service.description}>{service.name || 'N/A'}</td>
              <td>{service.category || 'N/A'}</td>
              <td>{service.coverage || 'N/A'}</td>
              <td>{service.interventionSla || 'N/A'}</td>
              <td>{service.quantity || 'N/A'}</td>
              <td className={service.covers ? 'ct-yes' : 'ct-no'}>
                <b>{service.covers ? 'Yes' : 'No'}</b>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {(contract.description.trim() !== '' || domains.length > 0) && (
        <>
          <label>Description</label>
          <pre className="ct-desc">
            {contract.description}
            {domains.length > 0 && (
              <>
                {'\n'}Domains:{'\n'}
                {domains.join('\n')}
              </>
            )}
          </pre>
        </>
      )}

      <button type="button" className="btn btn-info btn-sm" onClick={() => setShowModules(!showModules)}>
        Inactive Modules
      </button>

      {showModules &&
        (modules.length === 0 ? (
          <div className="ct-modules">No inactive modules.</div>
        ) : (
          <div className="ct-modules">
            {/*
             * Numele, nu iconita. Portalul aducea fiecare iconita de pe propriul
             * server cu `file_get_contents(base_url() . ...)` - o cerere HTTP a
             * serverului catre el insusi, cu verificarea certificatului stinsa,
             * pentru fiecare modul - si o lipea in pagina codificata. Numele
             * spune acelasi lucru si nu costa nimic.
             */}
            {modules.map((one) => (
              <span className="ct-module" key={one.name}>
                {one.name}
              </span>
            ))}
          </div>
        ))}
    </div>
  )
}
